import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Fragment, lazy, StrictMode, Suspense } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ApiAppParams } from 'typings/apiApp';
import { bridgeAtom, chatIdAtom, isOpenChatAtom, isWidgetReadyAtom, messagesAtom, store, tokenAtom } from '@/store';
import { APP_ID } from './constants';

const componentMapper = { app: lazy(() => import('@/modules/app/App.container')) };

const elementIdMapper = { app: APP_ID };

let root: Root | null = null;

export function initExternalModule(moduleName: keyof typeof componentMapper) {
  function renderModule(config: ApiAppParams) {
    let moduleContainer: HTMLElement | ShadowRoot | null = document.getElementById(elementIdMapper[moduleName]);
    if (root || !Object.keys(config).length) {
      return;
    }

    const Module = componentMapper[moduleName];

    if (moduleContainer) {
      moduleContainer.remove();
    }
    moduleContainer = document.createElement(elementIdMapper[moduleName]);
    moduleContainer.id = elementIdMapper[moduleName];

    const wrapper = document.getElementById('saywhatt-outlet');

    if (config.isEmbedded && wrapper) {
      wrapper.appendChild(moduleContainer);
    } else {
      document.body.appendChild(moduleContainer);
    }

    if (moduleName === 'app') {
      const shadow = moduleContainer.attachShadow({ mode: 'open' });
      moduleContainer = moduleContainer.shadowRoot;

      const cache = createCache({ container: shadow, key: 'css', prepend: true });

      root = createRoot(moduleContainer!);

      const Wrapper = import.meta.env.DEV ? Fragment : StrictMode;

      return root.render(
        <Wrapper>
          <CacheProvider value={cache}>
            <Suspense fallback={<Fragment />}>
              <Module config={config} />
            </Suspense>
          </CacheProvider>
        </Wrapper>
      );
    }

    moduleContainer = document.createElement('div');
    moduleContainer.id = elementIdMapper[moduleName];
    document.body.appendChild(moduleContainer);

    if (moduleContainer && Module) {
      const _root = createRoot(moduleContainer);
      _root.render(
        <Suspense fallback={<Fragment />}>
          <Module config={config} />
        </Suspense>
      );
    }
  }

  return renderModule;
}

export const cleanupModule = async (): Promise<void> => {
  if (root) {
    const pendingRoot = root;
    root = null;

    return new Promise((resolve, reject) => {
      queueMicrotask(() => {
        try {
          pendingRoot.unmount();
          store.set(tokenAtom, null);
          store.set(bridgeAtom, {} as any);
          store.set(messagesAtom, []);
          store.set(isOpenChatAtom, false);
          store.set(chatIdAtom, null);
          store.set(isWidgetReadyAtom, false);
          return resolve();
        } catch (error) {
          return reject(error);
        }
      });
    });
  }
};
